import {useCallback, useEffect, useRef, useState} from "react";
import _ from 'lodash';
import L from 'leaflet/dist/leaflet';
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import 'leaflet/dist/leaflet.css';
import HelperService from "../services/helper-service";
import {Dropdown} from "react-bootstrap";
import {Loader} from "@googlemaps/js-api-loader";

export default function MapPicker( {onChange=null, lat=null, lon=null, gps=true, geocode= true, height="300px", ...props} ){

    //https://cloud.maptiler.com/maps/
    const [initialPosition, setInitialPosition] = useState(null);
    const [locationSearch, setLocationSearch] = useState('');
    const [showLocationsList, setShowLocationsList] = useState(false);
    const [locationsList, setLocationsList] = useState([]);
    const gMapRef = useRef(null);
    const geocoderRef = useRef(null);
    const placesRef = useRef(null);
    const mapRef = useRef(null);
    const oldPos = useRef({ lat: lat, lon: lon });

    const DefaultIcon = L.icon({
        iconUrl: icon,
        shadowUrl: iconShadow,
        iconAnchor: [12, 41], // point of the icon which will correspond to marker's location
        popupAnchor:  [0, -41] // point from which the popup should open relative to the iconAnchor
    });
    const containerId = 'map-picker';



    //init position as its needed for map init
    useEffect(() => {
        getInitialPosition().then((res)=>{
            HelperService.log('init position');
            setInitialPosition(res);
        })
    }, [])

    useEffect(() => {
        const loader = new Loader({
            apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
            version: 'weekly',
            language: 'en',
            libraries: ["places", "geocoding"]
        });

        // loader.importLibrary('maps')
        //     .then(({Map}) => {
        //         gMapRef.current = Map;
        //     })
        //     .catch((e) => {
        //         console.log(e);
        //     });
        loader.importLibrary('geocoding')
            .then(({Geocoder}) => {
                geocoderRef.current = new Geocoder();
            })
            .catch((e) => {
                console.log(e);
            });
        loader.importLibrary('places')
            .then((PlacesLibrary) => {
                placesRef.current = PlacesLibrary;
            })
            .catch((e) => {
                console.log(e);
            });
    }, [])

    //init map
    useEffect(() => {
        if (!mapRef?.current && initialPosition?.lat && initialPosition?.lon){

            HelperService.log('init map');
            const position = initialPosition;

            const container = L.DomUtil.get(containerId);
            if (container != null) {
                container._leaflet_id = null;
            }
            mapRef.current = L.map(containerId).setView([position.lat,position.lon],17);
            L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png",
                {
                    attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                    maxZoom: 19,
                }
            ).addTo(mapRef.current);

            // init locate button
            if (gps){
                const LocationControl = L.Control.extend({
                    options: {
                        position: 'bottomleft' // Position the button top-right
                    },
                    onAdd: function () {
                        const controlsContainer = L.DomUtil.create('div', 'leaflet-bar');
                        controlsContainer.innerHTML = '<a class="clickable" id="get-location"><span class="mdi mdi-crosshairs-gps"></span></a>';
                        controlsContainer.onclick = function () {
                            HelperService.log(['locateMe']);
                            getCurrentLocation().then((pos)=>{
                                if (pos?.lat && pos?.lon){
                                    mapRef.current.setView([pos.lat,pos.lon], mapRef.current.getZoom());
                                }
                            });
                        }
                        return controlsContainer;
                    }
                });
                mapRef.current.addControl(new LocationControl());
            }

            //init marker
            L.Marker.prototype.options.icon = DefaultIcon;
            const newMarker = L.marker([position.lat,position.lon]).addTo(mapRef.current);

            mapRef.current.on('movestart', function(){
                let center = mapRef.current.getCenter();
                oldPos.current = { lat: center.lat, lon: center.lng };
            });
            mapRef.current.on('move', function(){
                newMarker.setLatLng(mapRef.current.getCenter());
            });
            mapRef.current.on('moveend', function(mapEvent){
                let center = mapRef.current.getCenter();
                let newLat = center.lat;
                let newLon = center.lng;
                if (oldPos?.current?.lat?.toFixed(6)!==newLat.toFixed(6) && oldPos?.current?.lon?.toFixed(6)!==newLon.toFixed(6)){
                    onMapPositionChanged(newLat, newLon, true);
                }
            });

            HelperService.respondToVisibility(container, ()=>{mapRef.current.invalidateSize();})
        }
    }, [initialPosition]);

    async function getCurrentLocation(){
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                position => {
                    if (position?.coords?.latitude && position?.coords?.longitude) {
                        resolve({
                            lat: position.coords.latitude,
                            lon: position.coords.longitude,
                        });
                    } else {
                        resolve(null);
                    }
                },
                err => {
                    HelperService.log(err);
                    resolve({
                        lat: 40.00058,
                        lon: -75.296791,
                    }); // Resolve with default when there is an error
                }
            );
        });
    }

    async function getInitialPosition(){
        let newLocation = null
        return {
            lat: newLocation?.lat || lat || 40.00058,
            lon: newLocation?.lon || lon || -75.296791,
        };
    }

    const extractDataFromSearchItem = (place) => {
        let city, state = null;
        place.address_components.forEach(component => {
            if (component.types.includes('sublocality_level_1') || component.types.includes('locality')) {
                city = component.long_name;
            }
            if (component.types.includes('administrative_area_level_1') || component.types.includes('country')) {
                state = component.long_name;
            }
        });
        return {
            id: place.place_id,
            name: place.name || place.formatted_address,
            address: place.formatted_address,
            lat: place.geometry.location.lat(),
            lon: place.geometry.location.lng(),
            city: city,
            state: state
        };
    }

    const onMapPositionChanged = async (lat, lon, triggerEvent=true) => {
        if (!mapRef.current || !lat || !lon){
            return true;
        }
        HelperService.log(['onMapPositionChanged', lat, lon, triggerEvent]);
        //only change value when the value is different than the first one.
        if (triggerEvent && onChange && typeof onChange === 'function') {
            let address, name, city, state = null;
            if (geocode){
                await new Promise((resolve, reject) => {
                    geocoderRef.current.geocode( { location: {lat: parseFloat(lat), lng: parseFloat(lon)}, fulfillOnZeroResults: true },
                        (results, status) => {
                            if (status !== 'OK') {
                                resolve();
                                return;
                            }
                            if (!results.length) {
                                resolve();
                                return;
                            }
                            const locations = results.map((place) => {
                                return extractDataFromSearchItem(place);
                            });
                            if (locations && locations.length) {
                                name = locations[0].name;
                                address = locations[0].address;
                                state = locations[0].state;
                                city = locations[0].city;
                            }
                            resolve();
                        }
                    );
                });
            }

            const returnParams = {
                lat: lat,
                lon: lon,
                name: name,
                address: address,
                city: city,
                state: state
            };
            HelperService.log(returnParams);
            onChange(returnParams);
        }
        return true;
    }

    function searchLocations(query) {
        return new Promise((resolve, reject) => {
            let AS = new placesRef.current.AutocompleteService();
            AS.getPlacePredictions( {
                    input: query,
                    language: 'en'
                },
                (results, status) => {
                    if (status !== 'OK') {
                        resolve([]);
                        return;
                    }
                    if (!results.length) {
                        resolve([]);
                        return;
                    }
                    const locations = results.map((place) => {
                        return {
                            id: place.place_id,
                            name: place.description,
                            address: null,
                            lat: null,
                            lon: null,
                            city: null,
                            state: null
                        };
                    });
                    resolve(locations);
                }
            );
        });
    }

    const debouncedSearchLocations = useCallback(_.debounce((query, setLocationList, setShowLocationsList) => {
        searchLocations(query).then((res) => {
            setLocationList(res);
            setShowLocationsList(true);
        });
    }, 50), []);

    function onLocationSearchChange(e){
        setLocationSearch(e?.target?.value)
        if (e?.target?.value?.length > 2){
            debouncedSearchLocations(e.target.value+', USA', setLocationsList, setShowLocationsList);
        }
    }

    function clearLocationsSearch(){
        setLocationSearch('');
        setLocationsList([]);
        setShowLocationsList(false);
    }

    const onSearchLocationSelected = async (place, triggerEvent=true) => {
        if (!place?.lat || !place?.lon){
            if (place.id){
                place = await new Promise((resolve, reject) => {
                    const PS = new placesRef.current.PlacesService(document.createElement('div'));
                    PS.getDetails( {
                            placeId: place.id,
                        },
                        (result, status) => {
                            if (status !== 'OK') {
                                resolve(place);
                                return;
                            }
                            if (!result) {
                                resolve(place);
                                return;
                            }
                            resolve(extractDataFromSearchItem(result));
                        }
                    );
                });
            }
        }
        if (!place?.lat || !place?.lon){
            return true;
        }
        if (mapRef.current){
            mapRef.current.setView([place.lat,place.lon], mapRef.current.getZoom());
        }

        HelperService.log(['onSearchLocationSelected', place?.lat, place?.lon, triggerEvent]);

        if (triggerEvent && onChange && typeof onChange === 'function') {
            const returnParams = {
                lat: place.lat,
                lon: place.lon,
                address: place.address,
                city: place.city,
                state: place.state,
            };
            HelperService.log(returnParams);
            onChange(returnParams);
        }
        return true;
    }

    const SearchResultCell = ({place}) => {
        return <div className="p-2" onClick={()=>{onSearchLocationSelected(place); setShowLocationsList(false)}}>
                <div><strong>{place.name}</strong></div>
                <div className="text-secondary">{place.address}</div>
            </div>
    }

    return (
        <>
            <div className="form-group mb-1">
                <div className="input-group">
                    <input type="text" name="search" className="form-control" placeholder="Enter city, address" value={locationSearch} onChange={onLocationSearchChange} />
                    {showLocationsList && <button type="button" className="btn btn-secondary text-white" onClick={clearLocationsSearch}><span className="mdi mdi-close"></span></button>}
                </div>
                <Dropdown show={showLocationsList && locationsList?.length > 0} onMouseLeave={undefined}>
                    <Dropdown.Menu className="w-100">
                        {locationsList?.length>0 && locationsList.map(place =>
                            <Dropdown.Item key={place.id}><SearchResultCell place={place} /></Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
                {showLocationsList && locationsList?.length === 0 && <div className="text-muted">We couldn't find the place you're looking for.</div>}
            </div>
            <div id={containerId} style={{height: height, zIndex: 0}}></div>
        </>
    );
}
